<template>
  <div class="help-center-post-tree-leaves__component">
    <div
      class="help-center-post-tree-leaves__columns"
      :class="columnsClass"
      :style="columnStyles"
    >
      <a
        v-for="post in posts"
        :key="post.url"
        :href="post.url"
        class="help-center-post-tree-leaves__link"
      >
        <i
          v-if="post.icon"
          class="help-center-post-tree-leaves__icon"
          :class="post.icon"
        />
        <p class="help-center-post-tree-leaves__title">
          {{ post.title }}
        </p>
      </a>
    </div>
    <a
      v-if="hasMore"
      :href="category.url"
      class="help-center-post-tree-leaves__more"
    >
      See More
    </a>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  nested: {
    type: Boolean,
    default: false
  },
  hasSiblings: {
    type: Boolean,
    default: false
  },
  category: {
    type: Object,
    required: true
  },
  postMap: {
    type: Object,
    required: true
  }
})

const columns = computed(() => {
  if (props.nested) {
    return 1
  } else if (props.category.posts.length > 40) {
    return 4
  } else {
    return 2
  }
})

const columnsClass = computed(() => {
  if (columns.value === 1) {
    return 'help-center-post-tree-leaves__columns--1'
  } else if (columns.value === 2) {
    return 'help-center-post-tree-leaves__columns--2'
  } else if (columns.value === 4) {
    return 'help-center-post-tree-leaves__columns--4'
  } else {
    throw new Error('Unexpected number of columns!')
  }
})

const columnStyles = computed(() => {
  return {
    '--posts-per-column-when-two-columns': postsPerColumnWhenTwoColumns.value,
    '--posts-per-column-when-four-columns': postsPerColumnWhenFourColumns.value
  }
})

const postsPerColumnWhenTwoColumns = computed(() => {
  return Math.ceil(posts.value.length/2)
})

const postsPerColumnWhenFourColumns = computed(() => {
  return Math.ceil(posts.value.length/4)
})

const limit = computed(() => {
  if (props.hasSiblings) {
    return columns.value*6
  } else {
    return null
  }
})

const posts = computed(() => {
  const posts = []
  for (const postUrl of props.category.posts) {
    posts.push(props.postMap[postUrl])
    if (limit.value && posts.length >= limit.value) {
      break
    }
  }

  return posts
})

const hasMore = computed(() => {
  return limit.value && props.category.posts.length > limit.value
})
</script>

<style lang="scss">
@import 'help-center/mixins';

.help-center-post-tree-leaves {
  &__component {
    gap: $helpCenterMobileVerPadding;
    display: flex;
    flex-direction: column;

    @include help-center-breakpoint(md) {
      gap: 16px;
    }
  }

  &__columns {
    display: grid;
    row-gap: $helpCenterMobileVerPadding;
    column-gap: 64px;
    justify-items: start;

    @include help-center-breakpoint(md) {
      row-gap: 16px;
    }

    &--2 {
      grid-template-columns: 1fr;

      @include help-center-breakpoint(sm) {
        grid-auto-flow: column;
        grid-template-rows: repeat(var(--posts-per-column-when-two-columns), auto);
        grid-template-columns: 1fr 1fr;
      }
    }

    &--4 {
      grid-template-columns: 1fr;

      @include help-center-breakpoint(sm) {
        grid-auto-flow: column;
        grid-template-rows: repeat(var(--posts-per-column-when-two-columns), auto);
        grid-template-columns: 1fr 1fr;
      }

      @include help-center-breakpoint(lg) {
        grid-template-rows: repeat(var(--posts-per-column-when-four-columns), auto);
        grid-template-columns: 1fr 1fr 1fr 1fr;
      }
    }
  }

  &__link {
    @include help-center-accent-link($selector: '.help-center-post-tree-leaves__title');
    gap: 8px;
    display: flex;
    align-items: center;
  }

  &__icon {
    @include help-center-icon(16);
    flex: none;
    color: var(--help-center-muted);
  }

  &__title {
    @include help-center-font(15);
    text-wrap: balance;
  }

  &__more {
    @include help-center-muted-link;
    @include help-center-font(12);
    width: fit-content;
    font-weight: var(--help-center-bold);
    text-transform: uppercase;
  }
}
</style>
